import type { LucideIcon } from 'lucide-react';
import {
  BarChart3, Boxes, Briefcase, Bug, Building2, CalendarCheck, CalendarDays, CheckCheck, Clock, Compass, CreditCard,
  FolderKanban, Inbox, LayoutDashboard, ListChecks, Mail, Network, Server, Settings, Shield, Sun, Sunrise, UserCircle,
  Users2, Vote,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// The one navigation (web app v4 plan §5.1): the desktop rail and the mobile
// drawer both render these groups, in this order, with these labels and
// icons. Who sees an entry is decided by lib/planGates' isTenantNavItemVisible
// (role, then plan, then permission), the same rules the route guards use.
// ---------------------------------------------------------------------------

export interface NavEntry {
  /** Stable name for tests and the orphan check. */
  id: string;
  label: string;
  path: string;
  icon: LucideIcon;
  /** A count beside the label: Review's "needs your yes". */
  badge?: 'review';
}

export interface NavGroup {
  id: string;
  label: string;
  entries: readonly NavEntry[];
}

export const TENANT_NAV: readonly NavGroup[] = [
  {
    // Assistants only (planGates), at the top.
    id: 'assistant',
    label: 'Assistant',
    entries: [
      { id: 'executives', label: 'Executives', path: '/assistant', icon: Users2 },
      { id: 'workspace', label: 'Workspace', path: '/assistant/workspace', icon: Briefcase },
      { id: 'profiles', label: 'Executive profiles', path: '/assistant/profiles', icon: UserCircle },
    ],
  },
  {
    id: 'day',
    label: 'Day',
    entries: [
      { id: 'today', label: 'Today', path: '/', icon: Sun },
      // The classic day's own pages, shown only to people not trying the new
      // Today, whose page has all three (isClassicDayEntryVisible).
      { id: 'brief', label: 'The Brief', path: '/brief', icon: Sunrise },
      { id: 'plan', label: 'Plan', path: '/plan', icon: CalendarCheck },
      { id: 'close-day', label: 'Close the day', path: '/close-day', icon: CheckCheck },
      { id: 'focus', label: 'Focus', path: '/focus', icon: Clock },
      { id: 'calendar', label: 'Calendar', path: '/calendar', icon: CalendarDays },
    ],
  },
  {
    id: 'work',
    label: 'Work',
    entries: [
      { id: 'review', label: 'Review', path: '/review', icon: Inbox, badge: 'review' },
      { id: 'work', label: 'Work', path: '/work', icon: ListChecks },
      { id: 'projects', label: 'Projects', path: '/projects', icon: FolderKanban },
    ],
  },
  {
    id: 'direction',
    label: 'Direction',
    entries: [
      { id: 'drivers', label: 'Drivers', path: '/drivers', icon: Compass },
      { id: 'decisions', label: 'Decisions', path: '/decisions', icon: Vote },
      { id: 'insights', label: 'Insights', path: '/insights', icon: BarChart3 },
    ],
  },
];

/**
 * Pinned under the groups. Admins get Organisation (Teams is a section of it);
 * anyone else whose plan has teams gets Teams on its own (plan §4, item 2).
 */
export const TENANT_FOOTER: readonly NavEntry[] = [
  { id: 'settings', label: 'Settings', path: '/settings', icon: Settings },
  { id: 'organisation', label: 'Organisation', path: '/organisation', icon: Building2 },
  { id: 'teams', label: 'Teams', path: '/organisation/teams', icon: Network },
];

export const PLATFORM_NAV: readonly NavGroup[] = [
  {
    id: 'platform',
    label: 'Platform',
    entries: [
      { id: 'platform-overview', label: 'Overview', path: '/platform', icon: LayoutDashboard },
      { id: 'tenants', label: 'Tenants', path: '/platform/tenants', icon: Building2 },
    ],
  },
  {
    id: 'billing',
    label: 'Billing',
    entries: [
      { id: 'billing-centre', label: 'Billing Centre', path: '/platform/billing', icon: CreditCard },
      { id: 'products', label: 'Products & Pricing', path: '/platform/products', icon: Boxes },
      { id: 'email', label: 'Email Settings', path: '/platform/email', icon: Mail },
    ],
  },
  {
    id: 'system',
    label: 'System',
    entries: [
      { id: 'security', label: 'Security & Diagnostics', path: '/platform/security', icon: Shield },
      { id: 'bug-reports', label: 'Bug Reports', path: '/platform/bug-reports', icon: Bug },
      { id: 'releases', label: 'Client Releases', path: '/platform/releases', icon: Server },
    ],
  },
];

/** The classic day's pages: in the nav only while the person isn't trying the new Today. */
export const CLASSIC_DAY_PATHS: ReadonlySet<string> = new Set(['/brief', '/plan', '/close-day']);

/**
 * Whether one of the classic day's pages shows: never on the new Today (its
 * page has all three), The Brief for executives (admins have none), Plan when
 * Settings → Appearance turns it on.
 */
export function isClassicDayEntryVisible(
  path: string, o: { newToday: boolean | 'loading'; adminRole: boolean; planEntry: boolean },
): boolean {
  if (o.newToday !== false) return false;
  if (path === '/brief') return !o.adminRole;
  if (path === '/plan') return o.planEntry;
  return true;
}

/** The groups with only the entries this person sees; empty groups go. */
export function visibleGroups(groups: readonly NavGroup[], isVisible: (path: string) => boolean): NavGroup[] {
  return groups
    .map(g => ({ ...g, entries: g.entries.filter(e => isVisible(e.path)) }))
    .filter(g => g.entries.length > 0);
}
